import { observableValue, type ISettableObservable } from '@vscode/observables';

export interface LocalStorageObservableOptions<T> {
  readonly storageKey: string;
  readonly defaultValue: T;
  /** Serialize value to string for localStorage. Defaults to JSON.stringify. */
  readonly serialize?: (value: T) => string;
  /** Deserialize string from localStorage. Defaults to JSON.parse. */
  readonly deserialize?: (raw: string) => T;
}

/**
 * Creates an observable that persists its value to localStorage.
 * The value is loaded from localStorage on creation (if present),
 * and saved to localStorage whenever it changes.
 */
export function createLocalStorageObservable<T>(
  owner: object,
  options: LocalStorageObservableOptions<T>
): ISettableObservable<T> {
  const {
    storageKey,
    defaultValue,
    serialize = JSON.stringify,
    deserialize = JSON.parse,
  } = options;

  // Try to load from localStorage
  let initialValue = defaultValue;
  try {
    const stored = localStorage.getItem(storageKey);
    if (stored !== null) {
      initialValue = deserialize(stored);
    }
  } catch {
    // Ignore parse errors, use default
  }

  const obs = observableValue<T>(owner, initialValue);

  // Wrap the set method to persist to localStorage
  const originalSet = obs.set.bind(obs);
  obs.set = (value: T, tx: undefined) => {
    try {
      localStorage.setItem(storageKey, serialize(value));
    } catch {
      // Ignore storage errors (quota exceeded, etc.)
    }
    originalSet(value, tx);
  };

  return obs;
}
